/**
 * Chef-side notification bell — polls /api/notifications/unread every 60s
 * + on window focus.  Dropdown shows recent unread items; click → mark read
 * + navigate to /settings?tab=tickets (or the relevant deep link later).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bell, BellRing, Loader2, CheckCheck } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";

const POLL_INTERVAL_MS = 60000;

export default function NotificationBell() {
  const [unread, setUnread] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const intervalRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await axios.get(`${API}/notifications/unread`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setUnread(res.data.notifications || []);
      setCount(res.data.unread_count || 0);
    } catch {
      /* silent — bell is best-effort */
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    intervalRef.current = setInterval(fetchUnread, POLL_INTERVAL_MS);
    const onFocus = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(intervalRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchUnread]);

  const markOne = async (id) => {
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await axios.post(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${session?.access_token || ""}` },
      });
      setUnread((prev) => prev.filter((n) => n.id !== id));
      setCount((c) => Math.max(0, c - 1));
    } finally {
      setBusy(false);
    }
  };

  const markAll = async () => {
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${session?.access_token || ""}` },
      });
      setUnread([]);
      setCount(0);
    } finally {
      setBusy(false);
    }
  };

  const HasUnread = count > 0;
  const Icon = HasUnread ? BellRing : Bell;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors",
            HasUnread
              ? "text-amber-300 hover:bg-amber-500/10"
              : "text-slate-400 hover:bg-slate-800"
          )}
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Icon className={cn("h-4 w-4", HasUnread && "animate-pulse")} />
          {HasUnread && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full bg-amber-500 text-slate-950 text-[0.6rem] font-bold flex items-center justify-center"
              data-testid="notification-bell-count"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-80 max-h-[60vh] overflow-y-auto bg-slate-900 border border-slate-700 p-0"
        data-testid="notification-bell-menu"
      >
        <div className="px-3 py-2.5 border-b border-slate-800 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-300">Notifications</span>
          {HasUnread && (
            <button
              type="button" onClick={markAll} disabled={busy}
              className="text-[0.65rem] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="h-3 w-3" />
              Mark all read
            </button>
          )}
        </div>
        {busy && (
          <div className="py-6 text-center text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin inline" />
          </div>
        )}
        {!busy && unread.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-slate-500 italic">
            You&apos;re all caught up.
          </div>
        ) : (
          unread.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => markOne(n.id)}
              className="w-full text-left px-3 py-2.5 border-b border-slate-800 last:border-0 hover:bg-slate-800/70 transition-colors block"
              data-testid={`notification-item-${n.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-xs font-semibold text-amber-300">{n.title}</span>
                <span className="text-[0.6rem] text-slate-500 font-mono whitespace-nowrap">
                  {new Date(n.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              {n.body && (
                <p className="text-[0.7rem] text-slate-400 leading-relaxed mt-1 line-clamp-2">
                  {n.body}
                </p>
              )}
            </button>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
